import React from 'react';

export const formatCoordinates = (latlng) => {
  const latSuffix = latlng.lat >= 0 ? 'N' : 'S';
  const lngSuffix = latlng.lng >= 0 ? 'E' : 'W';
  const formattedLat = `${Math.abs(latlng.lat.toFixed(3))} ${latSuffix}`;
  const formattedLng = `${Math.abs(latlng.lng.toFixed(3))} ${lngSuffix}`;
  return { lat: formattedLat, lng: formattedLng };
};

export const riskStyles = {
  convection: {
    // 1: { text: 'Light', color: '#2E8B57' },
    1: { text: 'Light', color: '#dfcd1a' },
    2: { text: 'Moderate', color: '#FFA500' },
    3: { text: 'Severe', color: '#FF4500' },
    // 5: { text: 'Overshoot', color: 'purple' },
    default: { text: 'Unknown', color: 'grey' },
  },
  ice: {
    1: { text: 'Light', color: '#7fd4f9' },
    2: { text: 'Moderate', color: '#1664c0' },
    3: { text: 'Severe', color: '#311c93' },
    default: { text: 'Unknown Icing', color: 'grey' },
  },
  turbulence: {
    1: { text: 'Light', color: '#ffd700' },
    2: { text: 'Moderate', color: '#ff8c00' },
    3: { text: 'Severe', color: '#ff0000' },
    default: { text: 'Unknown Turbulence', color: 'grey' },
  },
  dust: {
    1: { text: 'Low', color: '#c29e70' },
    2: { text: 'Medium', color: '#8c602f' },
    3: { text: 'High', color: '#533234' },
    default: { text: 'Unknown Dust', color: 'grey' },
  },
  nowcasting: {
    // Valores en mm/h
    '0.08': { text: 'Very Light Rain', color: '#0000FE' },
    '0.5': { text: 'Light Rain', color: '#3265FE' },
    '1': { text: 'Moderate Rain', color: '#0CBCFE' },
    '2': { text: 'Heavy Rain', color: '#00FEFE' },
    '4': { text: 'Very Heavy Rain', color: '#00FE00' },
    '8': { text: 'Intense Rain', color: '#00A900' },
    '16': { text: 'Storm', color: '#FEFF00' },
    '32': { text: 'Severe Storm', color: '#FEBF00' },
    '160': { text: 'Extreme Storm', color: '#FF0000' },
    default: { text: 'No Precipitation', color: 'grey' },
  },
};

const BaseTooltip = ({ title, value, color, coordinates, children }) => {
  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      padding: window.innerWidth < 600 ? '8px' : '10px',
      borderRadius: '16px',
      fontSize: window.innerWidth < 600 ? '12px' : '13px',
      minWidth: window.innerWidth < 600 ? '160px' : '200px',
      maxWidth: '90vw',
      color: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.5)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        color: '#444',
        padding: '6px',
        fontWeight: '800',
        marginBottom: '10px',
        borderRadius: '8px',
        textAlign: 'center',
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        fontSize: '10px'
      }}>
        {title}
      </div>
      <div style={{
        padding: '5px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        marginBottom: '10px'
      }}>
        <span style={{
          color: color || '#1a1a1a',
          fontSize: window.innerWidth < 600 ? '16px' : '18px',
          fontWeight: '900',
          display: 'block',
          textAlign: 'center',
          marginBottom: '5px',
          letterSpacing: '-0.02em'
        }}>
          {value}
        </span>
        {children}
      </div>
      {coordinates && (
        <table style={{
          width: '100%',
          marginTop: '0',
          color: '#3452b1',
          fontSize: '10px',
          borderCollapse: 'collapse',
          fontWeight: '500'
        }}>
          <tbody>
            <tr>
              <td style={{ padding: '2px 5px', opacity: 0.6 }}>LAT</td>
              <td style={{ padding: '2px 5px', textAlign: 'right', fontWeight: 'bold' }}>
                {coordinates.lat}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '2px 5px', opacity: 0.6 }}>LON</td>
              <td style={{ padding: '2px 5px', textAlign: 'right', fontWeight: 'bold' }}>
                {coordinates.lng}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export const ConvectionTooltip = ({ riskLevel, coordinates }) => {
  const { text, color } = riskStyles.convection[riskLevel] || riskStyles.convection.default;

  return (
    <BaseTooltip
      title="Convection Risk"
      value={text}
      color={color}
      coordinates={coordinates}
    />
  );
};

export const IceTooltip = ({ riskLevel, coordinates }) => {
  const { text, color } = riskStyles.ice[riskLevel] || riskStyles.ice.default;

  return (
    <BaseTooltip
      title="Icing Risk"
      value={text}
      color={color}
      coordinates={coordinates}
    />
  );
};

export const TurbulenceTooltip = ({ riskLevel, coordinates }) => {
  const { text, color } = riskStyles.turbulence[riskLevel] || riskStyles.turbulence.default;
  return (
    <BaseTooltip
      title="Turbulence Level"
      value={text}
      color={color}
      coordinates={coordinates}
    />
  );
};

export const DustTooltip = ({ riskLevel, coordinates }) => {
  const { text, color } = riskStyles.dust[riskLevel] || riskStyles.dust.default;
  return (
    <BaseTooltip
      title="Dust Severity"
      value={text}
      color={color}
      coordinates={coordinates}
    />
  );
};

export const NowcastingTooltip = ({ precipitation, coordinates }) => {
  const precipitationKey = precipitation?.toString();
  const { text, color } = riskStyles.nowcasting[precipitationKey] || riskStyles.nowcasting.default;

  return (
    <BaseTooltip
      title="Precipitation"
      value={text}
      color={color}
      coordinates={coordinates}
    >
      {precipitation && (
        <div style={{
          textAlign: 'center',
          fontSize: '11px',
          marginTop: '3px',
          color: '#666'
        }}>
          {precipitation} mm/h
        </div>
      )}
    </BaseTooltip>
  );
};

export default {
  ConvectionTooltip,
  IceTooltip,
  TurbulenceTooltip,
  DustTooltip,
  NowcastingTooltip,
  formatCoordinates,
  riskStyles
};
